import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import {
  subscribeToProjects,
  createProject as createProjectService,
  updateProject as updateProjectService,
  deleteProject as deleteProjectService,
  isProjectInUse,
  getClients,
} from '@/lib/projects';
import type { Project, Client } from '@/types';

export const useProjects = (statusFilter: 'active' | 'archived' | 'completed' | 'all' = 'active') => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // プロジェクト一覧の購読
    const unsubscribe = subscribeToProjects(user.uid, statusFilter, (data) => {
      setProjects(data);
      setIsLoading(false);
    });

    // クライアント一覧の取得
    getClients(user.uid).then((data) => {
      setClients(data);
    }).catch((error) => {
      console.error('Failed to fetch clients:', error);
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [user, statusFilter]);

  const createProject = async (projectData: Omit<Project, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!user || isProcessing) return;

    setIsProcessing(true);
    try {
      await createProjectService(user.uid, projectData);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateProject = async (projectId: string, projectData: Partial<Project>) => {
    if (!user || isProcessing) return;

    setIsProcessing(true);
    try {
      await updateProjectService(user.uid, projectId, projectData);
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteProject = async (projectId: string) => {
    if (!user || isProcessing) return;

    // 使用中かチェック
    const inUse = await isProjectInUse(user.uid, projectId);
    if (inUse) {
      throw new Error('このプロジェクトは現在使用中のため削除できません');
    }

    setIsProcessing(true);
    try {
      await deleteProjectService(user.uid, projectId);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    projects,
    clients,
    isLoading,
    isProcessing,
    createProject,
    updateProject,
    deleteProject,
  };
};
